import { useTranslation } from 'react-i18next';
import type { NullableInstitutionType } from '@/types/platform';

/**
 * Translates a server-supplied enum value, falling back to the raw code.
 *
 * These vocabularies are owned by another repository and can grow before this one
 * is redeployed. A missing key must show `EDUCATION_AUTHORITY`, which is at least
 * true, rather than the key path or an empty cell.
 */
export function useEnumLabel(): (namespace: string, value: string | null | undefined) => string {
  const { t } = useTranslation();

  return (namespace, value) => {
    if (!value) return '—';
    const key = `platform.${namespace}.${value}`;
    const label = t(key);
    return label === key ? value : label;
  };
}

/**
 * The institution type, including the case where there isn't one.
 *
 * `institutionType` is nullable now. MFI, BANK, MERCHANT_ORGANIZATION and
 * CSKY_INTERNAL were retired from the contract and deliberately NOT remapped onto
 * one of the nine surviving types, because none of them means "a microfinance
 * institution" and a plausible-looking wrong value is worse than an obviously
 * legacy one. Those rows now carry null.
 *
 * An em dash would read as "we failed to load this", and falling back to the first
 * enum member would put a wrong classification on screen next to an institution
 * someone is about to suspend. So null gets its own words, saying what actually
 * happened, and the operator can go and reclassify the row.
 */
export function useInstitutionTypeLabel(): (value: NullableInstitutionType | undefined) => string {
  const { t } = useTranslation();
  const label = useEnumLabel();

  return (value) => {
    if (value === null) return t('platform.institutionType.RETIRED');
    if (value === undefined) return '—';
    return label('institutionType', value);
  };
}
